import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import QRCode from 'qrcode';
import { authService, MfaChallenge, SignedIn } from '../api/auth';
import { colors } from '../../../theme';

interface Props {
  challenge: MfaChallenge;
  onSignedIn: (result: SignedIn) => void;
  /** Back to the password form — the only way on once the session has expired. */
  onRestart: () => void;
}

/** The secret in groups of four, as authenticator apps show it. */
const groupKey = (secret: string) => (secret.match(/.{1,4}/g) ?? []).join(' ');

/**
 * Second step of sign-in — Operator MFA, CTO decision 12 Oct 2026
 * (ADM-18-FR-05: moving money and seeing minors' data require MFA).
 *
 * MFA_SETUP is the first sign-in of an ADMIN/MODERATOR without an
 * authenticator: the backend withholds the session until a code from the
 * newly added authenticator verifies. SOFTWARE_TOKEN_MFA is every sign-in
 * after that. The QR code is drawn in the browser from the otpauth URI; the
 * key is shown as well for anyone who cannot scan.
 */
const MfaChallengeForm: React.FC<Props> = ({ challenge, onSignedIn, onRestart }) => {
  const enrolling = challenge.challenge === 'MFA_SETUP';
  const [code, setCode] = useState('');
  const [qr, setQr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!enrolling || !challenge.otpauthUri) return;
    let live = true;
    QRCode.toDataURL(challenge.otpauthUri, { margin: 1, width: 200 })
      .then((url) => live && setQr(url))
      // No QR is not fatal: the key below can be typed in instead.
      .catch(() => live && setQr(null));
    return () => {
      live = false;
    };
  }, [enrolling, challenge.otpauthUri]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      onSignedIn(await authService.verifyMfa(challenge.session, code));
    } catch (err: any) {
      const message: string = err?.message || '';
      if (/session expired|sign in again/i.test(message)) {
        setExpired(true);
        setError('This sign-in took too long and has expired. Please sign in again.');
      } else if (/invalid verification code|code/i.test(message)) {
        setError("That code didn't match. Check your authenticator app and try again.");
      } else {
        setError("We couldn't verify the code right now. Please try again in a moment.");
      }
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        fontWeight={700}
        align="center"
        sx={{ mb: 0.75, color: 'text.primary' }}
      >
        {enrolling ? 'Set up two-step verification' : 'Two-step verification'}
      </Typography>
      <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 3 }}>
        {enrolling
          ? 'Operator accounts must use an authenticator app. Scan this code with an app such as Google Authenticator, Microsoft Authenticator or 1Password, then enter the 6-digit code it shows.'
          : 'Enter the 6-digit code from your authenticator app.'}
      </Typography>

      {enrolling && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          {qr && (
            <Box
              component="img"
              src={qr}
              alt="QR code to add your IQMaxx account to an authenticator app"
              sx={{ width: 200, height: 200, mb: 2 }}
            />
          )}
          {challenge.secretCode && (
            <>
              <Typography variant="body2" color="text.secondary">
                Can&apos;t scan? Enter this key in the app:
              </Typography>
              <Typography
                data-testid="mfa-secret-key"
                sx={{ fontFamily: 'monospace', fontWeight: 600, wordBreak: 'break-all', mt: 0.5 }}
              >
                {groupKey(challenge.secretCode)}
              </Typography>
            </>
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {expired ? (
        <Button fullWidth variant="contained" onClick={onRestart} sx={{ py: 1.5, borderRadius: 2 }}>
          Sign in again
        </Button>
      ) : (
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            fullWidth
            label="6-digit code"
            name="code"
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, '').slice(0, 6));
              setError(null);
            }}
            autoComplete="one-time-code"
            inputProps={{ inputMode: 'numeric', maxLength: 6 }}
            autoFocus
            sx={{ mb: 2 }}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              py: 1.5,
              fontWeight: 700,
              fontSize: '1rem',
              borderRadius: 2,
              backgroundColor: colors.primary.main,
              '&:hover': { backgroundColor: colors.primary.dark },
              mb: 2,
            }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Verify'}
          </Button>
          <Button
            fullWidth
            onClick={onRestart}
            size="small"
            sx={{ color: 'text.secondary', textTransform: 'none' }}
          >
            Use a different account
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default MfaChallengeForm;
